import {
  BookmarkToggleButton,
  CLEAR_FILTER_TOKEN,
  Column,
  ConditionalWrap,
  FILTER_CONCATENATE_TOKEN,
  PromptCard,
  PromptCardSkeleton,
  PromptTabs,
  Row,
  type CollectionFilterKeyType,
  type Filter,
  type Prompt,
} from "@1js/pl-card-components";
import type { Group } from "@1js/pl-types";
import { Filter24Regular } from "@fluentui/react-icons";
import * as React from "react";
import { LibraryContext } from "../../pages/prompts/allPromptsLibrary/LibraryContext";
import type { SearchResultsData } from "../../types";
import { Carousel } from "../Carousel/Carousel.web";
import { CollectionsContentSection } from "../MetaOs/CollectionsContentSection";
import { PromptDetailModal } from "../PromptDetail/PromptDetailModal.web";
import type { WebPrompt } from "../data";
import type { HandlePromptsTabSelected } from "./AllPrompts.web.data";
import { useStyles } from "./AllPrompts.web.styles";
import { EmptyStatePromptResults } from "./EmptyStatePromptResults";
import { Filters } from "./Filters.web";
import { ShowMore } from "./ShowMore.web";
import { useSuggestedPrompts } from "./SuggestedPrompts.hook.web";
import { useColumnStyle } from "./useColumnStyle.hook";

export type SuggestedPromptsProps = {
  isLoading: boolean;
  groupPromptStatus: string;
  enableCarousel: boolean;
  skeletonCount: number;
  promptsToDisplay: Prompt[];
  promptRefs: React.MutableRefObject<
    Map<string, React.RefObject<HTMLDivElement>>
  >;
  currentFocusedPromptId: string | null;
  shareToWorkgroup: (prompts: Prompt[]) => void;
  handleShareViaCopyLinkClicked?: (prompt: Prompt) => Promise<void>;
  handleShareViaEmailClicked?: (prompt: Prompt) => Promise<void>;
  handleCardClick: (prompt: Prompt) => void;
  onPromptBookmarkToggled: (prompt: Prompt) => Promise<void>;
  onDelete?: (prompt: Prompt) => void;
  setLastFocusedPromptId: (promptId: string) => void;
  setCurrentFocusedPromptId: (promptId: string) => void;
  prompts: Prompt[];
  groups?: Group[];
  isShowHasMore: boolean;
  isMetaOsV2?: boolean;
  hasMore?: boolean;
  handleCardFocus: () => void;
  onMoreClicked: () => void;
  resultsAnnouncementId: string;
  swipe: {
    setSwipedState: (swiped: boolean) => void;
  };
  isWorkgroupPublishingEnabled: boolean;
  dialogOpen: boolean;
  handleDialogClose: () => void;
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  activePrompt: WebPrompt | undefined;
  morePrompts: WebPrompt[];
  onSharePromptClicked: ((prompt: Prompt) => void) | undefined;
  onCopyPromptClicked: ((prompt: Prompt) => void) | undefined;
  onTryInAppBadgeAction:
    | ((
        prompt: Prompt,
        product: string,
        tagRef: React.MutableRefObject<null>
      ) => void)
    | undefined;
  isShowFilters: boolean;
  onBookmarkToggled: (() => void) | undefined;
  filters: Filter[];
  selectedFacets: Record<string, string[]>;
  handleFilterChange: (
    selectedValues: Record<string, string[]>,
    filters: Filter[]
  ) => void;
  onFilterChanged: (filters: Record<string, string[]>) => void;
  children: React.ReactNode[] | React.ReactNode;
  setworkgroupPublisherTeachingPopoverTarget: React.Dispatch<
    React.SetStateAction<HTMLElement | null>
  >;
  handlePromptsTabSelected: HandlePromptsTabSelected;
  onFiltersCleared: () => void;
  onFiltersOpenChanged: (isOpen: boolean, filters: Filter[]) => void;
  handleKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  searchResultsData?: SearchResultsData;
  isHeroCard?: boolean;
  isMetaOsV2Home?: boolean;
  disabledFilters?: Record<string, boolean>;
  isMetaOsV2HomePage?: boolean;
  onCollectionTileClick?: (
    filterKey: CollectionFilterKeyType,
    item: string
  ) => void;
  selectedFilterValue?: string | null;
  selectedTab?: PromptTabs;
};

export const SuggestedPrompts: React.FC<SuggestedPromptsProps> = ({
  isLoading,
  currentFocusedPromptId,
  groupPromptStatus,
  enableCarousel,
  skeletonCount,
  promptsToDisplay,
  promptRefs,
  shareToWorkgroup,
  handleShareViaCopyLinkClicked,
  handleShareViaEmailClicked,
  handleCardClick,
  onPromptBookmarkToggled,
  onDelete,
  setLastFocusedPromptId,
  setCurrentFocusedPromptId,
  prompts,
  groups,
  isShowHasMore,
  isMetaOsV2,
  searchResultsData,
  hasMore,
  handleCardFocus,
  onMoreClicked,
  resultsAnnouncementId,
  swipe,
  isWorkgroupPublishingEnabled,
  dialogOpen,
  handleDialogClose,
  setDialogOpen,
  activePrompt,
  morePrompts,
  onSharePromptClicked,
  onCopyPromptClicked,
  onTryInAppBadgeAction,
  isShowFilters,
  onBookmarkToggled,
  filters,
  selectedFacets,
  handleFilterChange,
  children,
  setworkgroupPublisherTeachingPopoverTarget,
  handlePromptsTabSelected,
  onFiltersOpenChanged,
  onFiltersCleared,
  handleKeyDown,
  isHeroCard,
  disabledFilters,
  onCollectionTileClick,
  isMetaOsV2HomePage,
  selectedFilterValue,
  selectedTab,
}: SuggestedPromptsProps) => {
  const styles = useStyles();
  const {
    isFlightActive,
    handleOnDelete,
    handleBookmarkToggleOnClick,
    chooseBestProductForHandoff,
  } = useSuggestedPrompts({
    handlePromptsTabSelected,
    onBookmarkToggled,
    setDialogOpen,
    onDelete,
  });
  const { state } = React.useContext(LibraryContext);

  const appearance = isFlightActive("isMosAppPromptCardV2Enabled")
    ? "web-v3"
    : "web";
  const cardColStyles = useColumnStyle(isHeroCard, isMetaOsV2);

  // Check for inactive filters(only default select all is applied)
  const inActiveFilter = Object.values(selectedFacets).every(
    (values) =>
      Array.isArray(values) &&
      values.every((value) => {
        const [firstToken] = value.split(FILTER_CONCATENATE_TOKEN);
        return firstToken === CLEAR_FILTER_TOKEN;
      })
  );

  // Showing an empty state when no prompts are available without any filters on load
  if (
    isMetaOsV2 &&
    selectedTab == PromptTabs.WorkplacePrompts &&
    inActiveFilter &&
    promptsToDisplay.length === 0
  ) {
    return (
      <>
        <CollectionsContentSection
          searchResultsData={searchResultsData}
          onCollectionTileClick={onCollectionTileClick}
          isMetaOsV2HomePage={isMetaOsV2HomePage}
          selectedTab={selectedTab}
          titleOverride={
            selectedFilterValue == null ? undefined : selectedFilterValue
          }
        />
        <EmptyStatePromptResults navItem={PromptTabs.WorkplacePrompts} />
      </>
    );
  }

  return (
    <>
      {isMetaOsV2 && selectedTab && (
        <CollectionsContentSection
          searchResultsData={searchResultsData}
          onCollectionTileClick={onCollectionTileClick}
          isMetaOsV2HomePage={isMetaOsV2HomePage}
          selectedTab={selectedTab}
          titleOverride={
            selectedFilterValue == null ? undefined : selectedFilterValue
          }
        />
      )}
      {isShowFilters && (
        <div className={styles.filtersRow}>
          {!isMetaOsV2 &&
            isFlightActive("isBookmarksEnabled") &&
            !isFlightActive("isPromptSavingEnabled") && (
              <BookmarkToggleButton
                className={styles.bookmarkToggle}
                isLoading={isLoading}
                showBookmarkOnly={false}
                onClicked={handleBookmarkToggleOnClick}
              />
            )}
          <div className={styles.filtersContainer}>
            <div className={styles.filtersRow}>
              {isMetaOsV2 && <Filter24Regular className={styles.filterIcon} />}
              <Filters
                filters={filters}
                selectedFilters={selectedFacets}
                isLoading={isLoading}
                onFilterChanged={handleFilterChange}
                onCleared={onFiltersCleared}
                onOpenChanged={onFiltersOpenChanged}
                isMetaOsV2={isMetaOsV2}
                disabledFilters={disabledFilters}
                selectedFilterValue={selectedFilterValue}
              />
            </div>
          </div>
        </div>
      )}
      {children}
      {isLoading || groupPromptStatus === "loading" ? (
        <ConditionalWrap
          condition={!enableCarousel}
          wrapIfTrue={(children) => (
            <Row className={styles.cards} isMetaOsV2={isMetaOsV2}>
              {children}
            </Row>
          )}
        >
          {/* create skeletonCount number of PromptCardSkelton components */}
          {Array.from({ length: skeletonCount }).map((_, index) => (
            <ConditionalWrap
              key={index}
              condition={!enableCarousel}
              wrapIfTrue={(children) => (
                <Column key={index} size={3} className={cardColStyles}>
                  {children}
                </Column>
              )}
            >
              <PromptCardSkeleton
                appearance={appearance}
                isFavoriteEnabled={isFlightActive("isBookmarksEnabled")}
              />
            </ConditionalWrap>
          ))}
        </ConditionalWrap>
      ) : (
        <>
          <ConditionalWrap
            condition={!enableCarousel}
            wrapIfTrue={(children) => (
              <Row
                role="list"
                aria-label="Prompt"
                aria-describedby={resultsAnnouncementId}
                className={styles.cards}
                data-testid="prompt-cards"
                onKeyDown={handleKeyDown}
                isMetaOsV2={isMetaOsV2}
              >
                {children}
              </Row>
            )}
            wrapIfFalse={(children) => (
              <Carousel id="prompts" setSwipedState={swipe.setSwipedState}>
                {children}
              </Carousel>
            )}
          >
            {promptsToDisplay.map((prompt: Prompt, idx: number) => {
              if (!promptRefs.current.has(prompt.Id)) {
                promptRefs.current.set(prompt.Id, React.createRef());
              }

              const handleFocus = () => {
                setLastFocusedPromptId(prompt.Id);
                setCurrentFocusedPromptId(prompt.Id);
              };

              return (
                <ConditionalWrap
                  key={idx}
                  condition={!enableCarousel}
                  wrapIfTrue={(children) => (
                    <Column key={prompt.Id} size={3} className={cardColStyles}>
                      {children}
                    </Column>
                  )}
                  wrapIfFalse={(children) => (
                    <div className={styles.carousel}>{children}</div>
                  )}
                >
                  <PromptCard
                    appearance={appearance}
                    displayPosition={idx}
                    key={prompt.Id}
                    prompt={prompt}
                    isPromptFocused={currentFocusedPromptId === prompt.Id}
                    isFavoriteEnabled={isFlightActive("isBookmarksEnabled")}
                    isAgentPromptFavoriteEnabled={isFlightActive(
                      "isAgentPromptFavoriteEnabled"
                    )}
                    isPromptSavingEnabled={isFlightActive(
                      "isPromptSavingEnabled"
                    )}
                    isPromptSharingEnabled={isFlightActive(
                      "isPromptSharingEnabled"
                    )}
                    isPromptSharingViaEmailEnabled={isFlightActive(
                      "isPromptSharingViaEmailEnabled"
                    )}
                    isPromptSharingToWorkgroupEnabled={
                      isWorkgroupPublishingEnabled &&
                      (isFlightActive(
                        "isWorkgroupPublishingMicrosoftPromptsEnabled"
                      ) ||
                        prompt.Origin === "User")
                    }
                    isPromptLikingEnabled={isFlightActive(
                      "isPromptLikingEnabled"
                    )}
                    onShareToWorkgroupClicked={shareToWorkgroup}
                    onShareViaCopyLinkClicked={handleShareViaCopyLinkClicked}
                    onShareViaEmailClicked={handleShareViaEmailClicked}
                    onClicked={handleCardClick}
                    onFavoriteToggled={onPromptBookmarkToggled}
                    isBadgesEnabled={true}
                    ref={promptRefs.current.get(prompt.Id)}
                    onFocus={handleFocus}
                    onDelete={onDelete}
                    aria-posinset={idx + 1}
                    aria-setsize={prompts.length}
                    groups={groups}
                    setShareButtonTarget={
                      (idx === 0 &&
                        setworkgroupPublisherTeachingPopoverTarget) ||
                      undefined
                    }
                    extensionIconMap={state.extensionIconMap}
                    handoffProduct={chooseBestProductForHandoff(prompt)}
                  />
                </ConditionalWrap>
              );
            })}
          </ConditionalWrap>
          <ShowMore
            isShowHasMore={isShowHasMore}
            hasMore={hasMore}
            isLoading={isLoading}
            handleCardFocus={handleCardFocus}
            onMoreClicked={onMoreClicked}
          />
          {activePrompt ? (
            <PromptDetailModal
              dialogOpen={dialogOpen}
              setDialogOpen={setDialogOpen}
              handleDialogClose={handleDialogClose}
              prompt={activePrompt as WebPrompt}
              morePrompts={morePrompts}
              onMoreLikeThisClicked={handleCardClick}
              onBookmarkToggled={onPromptBookmarkToggled}
              onShareClicked={onSharePromptClicked}
              onCopyClicked={onCopyPromptClicked}
              isBookmarkEnabled={isFlightActive("isBookmarksEnabled")}
              isAgentPromptSavingEnabled={isFlightActive(
                "isAgentPromptSavingEnabled"
              )}
              isPromptSavingEnabled={isFlightActive("isPromptSavingEnabled")}
              isPromptSharingEnabled={isFlightActive("isPromptSharingEnabled")}
              isPromptSharingViaEmailEnabled={isFlightActive(
                "isPromptSharingViaEmailEnabled"
              )}
              isPromptSharingToWorkgroupEnabled={
                isWorkgroupPublishingEnabled &&
                (isFlightActive(
                  "isWorkgroupPublishingMicrosoftPromptsEnabled"
                ) ||
                  activePrompt.Origin === "User")
              }
              groups={groups}
              onShareToWorkgroupClicked={shareToWorkgroup}
              onDelete={handleOnDelete}
              isPromptCopyLinkEnabled={true}
              onTryInAppBadgeAction={onTryInAppBadgeAction || undefined}
              isPromptLikingEnabled={isFlightActive("isPromptLikingEnabled")}
            />
          ) : (
            <></>
          )}
        </>
      )}
    </>
  );
};
